/* eslint-disable max-len */
import { Entity } from '@sketchpixy/rubix/lib/L20n';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { change, destroy, initialize } from 'redux-form';
import { localizeHelpCenterLink } from '../../_config/utils.js';
import WearableIconCustom from '../../components/CustomIcons/WearableIconCustom.jsx';
import OperationalView from '../../components/OperationalView/OperationalView.jsx';
import PresentationalViewHeader from '../../components/PresentationalView/PresentationalViewHeader.jsx';
import WearableOperationalSection from '../../components/Wearable/WearableOperationalSection.jsx';
import WearableTableView from '../../components/Wearable/WearableTableView.jsx';
import * as ModalActions from '../../redux/actions/modal.actions';
import * as UtilsActions from '../../redux/actions/utils.actions';
import * as WearableActions from '../../redux/actions/wearable.actions';
import * as formatter from '../../_config/formatter.js';

let filterTimeout;

@connect((state) => ({
  routing: state.router,
  wearables: state.wearables,
  viewLoading: state.utils.viewLoading,
  themeName: state.settings.items.theme.data.themeName,
  form: state.form.WearableForm,
  isOperationalMode: state.wearables.isOperationalMode,
}))
class WearableManagement extends React.Component {

  async componentDidMount() {
    this.onFetchWearables();
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(WearableActions.setSelectedWearable({}));
    dispatch(WearableActions.resetWearablesData());
  }

  onFetchWearables() {
    const { dispatch } = this.props;
    dispatch(WearableActions.fetchWearables());
  }

  onDeleteWearableRequest(wearableId) {
    const { dispatch } = this.props;
    const params = {
      modalType: 'CONFIRM_TO_CONTINUE_MODAL',
      modalProps: {
        title: 'confirmBeforeContinue',
        body: <Entity entity="deleteWearableConfirm" />,
        onConfirmText: <Entity entity="yes" />,
        onConfirm: () => this.onDeleteWearable(wearableId),
        onCancelText: <Entity entity="no" />,
        onCancel: () => dispatch(ModalActions.hideModal()),
      },
    };
    dispatch(ModalActions.showModal(params));
  }

  async onDeleteWearable(wearableId) {
    const { dispatch } = this.props;
    try {
      dispatch(UtilsActions.setSpinnerVisibile(true));
      await dispatch(WearableActions.deleteWearable(wearableId));
      dispatch(UtilsActions.setSpinnerVisibile(false));
      this.onFetchWearables();
      this.onCloseOperationalSection();
      dispatch(ModalActions.showModal({
        modalType: 'SUCCESS_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="wearableDeletedSuccessfully" /></h6>),
        },
      }));
    } catch (error) {
      dispatch(UtilsActions.setSpinnerVisibile(false));
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="wearableActionError" /></h6>),
        },
      }));
    }
  }

  onSelectWearable(wearable) {
    const { dispatch } = this.props;
    dispatch(UtilsActions.setSpinnerVisibile(true));
    try {
      const wearableUser = wearable.user || null;
      
      // Format allowed areas for the selector
      const allowedAreasOptions = wearable.allowedAreas && _.isArray(wearable.allowedAreas)
        ? _.map(wearable.allowedAreas, area => ({ value: area, label: area.name }))
        : [];
      
      // Format not allowed areas for the selector
      const notAllowedAreasOptions = wearable.notAllowedAreas && _.isArray(wearable.notAllowedAreas)
        ? _.map(wearable.notAllowedAreas, area => ({ value: area, label: area.name }))
        : [];

      dispatch(initialize('WearableForm', {
        ...wearable,
        wearableUserSearch: wearableUser ? { value: wearableUser, label: wearableUser.email } : null,
        userId: wearable.user ? wearable.user.id : null,
        model: wearable.model,
        allowedAreasOptions: allowedAreasOptions,
        notAllowedAreasOptions: notAllowedAreasOptions,
      }));
      dispatch(UtilsActions.setSpinnerVisibile(false));
      dispatch(WearableActions.setSelectedWearable(wearable));
      dispatch(WearableActions.setWearablesOperationalMode(true));
    } catch (error) {
      dispatch(UtilsActions.setSpinnerVisibile(false));
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="wearableActionError" /></h6>),
        },
      }));
    }
  }

  onRefreshWearables() {
    this.onFetchWearables();
  }

  onFetchWearablesOnPage(page) {
    const { dispatch } = this.props;
    dispatch(WearableActions.fetchWearables(page));
  }

  onNewWearable() {
    const { dispatch } = this.props;
    dispatch(WearableActions.setSelectedWearable({}));
    dispatch(destroy('WearableForm'));
    dispatch(initialize('WearableForm', { type: 'BLE_BRACELET', model: 'MOKO SMART', secondsInsideNotAllowedAreasForAlarm: 1, secondsOutsideAllowedAreasForAlarm: 1 }));
    dispatch(WearableActions.setWearablesOperationalMode(true));
  }
  
  onCloseOperationalSection() {
    const { dispatch } = this.props;
    dispatch(WearableActions.setSelectedWearable({}));
    dispatch(WearableActions.setWearablesOperationalMode(false));
  }

  onSaveWearableData(wearableData) {
    // Prepare the DTO according to the API requirements
    const formattedWearableData = formatter.formatOutputData(formatter.WEARABLES, wearableData);

    if (wearableData && wearableData.id) {
      this.onEditWearable({ ...formattedWearableData, id: wearableData.id });
    } else {
      this.onCreateNewWearable(formattedWearableData);
    }
  }

  async onCreateNewWearable(wearableData) {
    const { dispatch } = this.props;
    try {
      dispatch(UtilsActions.setSpinnerVisibile(true));
      await dispatch(WearableActions.createWearable(wearableData));
      dispatch(UtilsActions.setSpinnerVisibile(false));
      dispatch(WearableActions.setWearablesOperationalMode(false));
      this.onFetchWearables();
      this.onCloseOperationalSection();
      dispatch(ModalActions.showModal({
        modalType: 'SUCCESS_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="wearableCreatedSuccessfully" /></h6>),
        },
      }));
    } catch (error) {
      dispatch(UtilsActions.setSpinnerVisibile(false));
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="wearableActionError" /></h6>),
        },
      }));
    }
  }

  async onEditWearable(wearableData) {
    const { dispatch } = this.props;
    try {
      dispatch(UtilsActions.setSpinnerVisibile(true));
      await dispatch(WearableActions.updateWearable(wearableData.id, wearableData));
      dispatch(UtilsActions.setSpinnerVisibile(false));
      dispatch(WearableActions.setWearablesOperationalMode(false));
      this.onFetchWearables();
      this.onCloseOperationalSection();
      dispatch(ModalActions.showModal({
        modalType: 'SUCCESS_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="wearableUpdatedSuccessfully" /></h6>),
        },
      }));
    } catch (error) {
      dispatch(UtilsActions.setSpinnerVisibile(false));
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="wearableActionError" /></h6>),
        },
      }));
    }
  }
  
  onResetWearablesFilters() {
    const { dispatch } = this.props;
    dispatch(WearableActions.resetWearablesFilters());
    this.onFetchWearables();
  }

  onSetFilter(field, value) {
    const { dispatch } = this.props;
    if (value && ((_.isNumber(value) && value >= 1) || (_.isArray(value) && !_.isEmpty(value)) || (_.isString(value) && value.length >= 2))) {
      dispatch(WearableActions.setWearablesFilter(field, value));
      if (filterTimeout) clearTimeout(filterTimeout);
      filterTimeout = setTimeout(async () => {
        this.onFetchWearables();
      }, 500);
    } else {
      dispatch(WearableActions.setWearablesFilter(field, null));
      this.onFetchWearables();
    }
  }

  onExportWearables(format) {
    const { dispatch } = this.props;
    dispatch(WearableActions.exportWearables(format));
  }

  onShowWearableInfoPage() {
    const helpURL = localizeHelpCenterLink('wearables');
    window.open(helpURL);
  }

  onSelectGuest(guest) {
    const { dispatch } = this.props;
    if (guest && guest.value) {
      dispatch(change('WearableForm', 'userId', guest.value.id));
    } else {
      dispatch(change('WearableForm', 'userId', null));
    }
    console.log('guest', guest);
  }

  onSelectAllowedAreas(areas) {
    const { dispatch } = this.props;
    if (areas && _.isArray(areas)) {
      const areaIds = _.map(areas, area => area.value.id);
      dispatch(change('WearableForm', 'allowedAreaIds', areaIds));
    } else {
      dispatch(change('WearableForm', 'allowedAreaIds', []));
    }
  }

  onSelectNotAllowedAreas(areas) {
    const { dispatch } = this.props;
    if (areas && _.isArray(areas)) {
      const areaIds = _.map(areas, area => area.value.id);
      dispatch(change('WearableForm', 'notAllowedAreaIds', areaIds));
    } else {
      dispatch(change('WearableForm', 'notAllowedAreaIds', []));
    }
  }

  
  render() {
    const { wearables, dispatch, themeName, viewLoading, isOperationalMode } = this.props;
    const canManageWearables = true //AbilityProvider.getAbilityHelper().hasPermission([PERMISSIONS.ALL], PERMISSION_ENTITIES.WEARABLES_MANAGEMENT);
    const numberOfWearables = wearables && wearables.data && wearables.data.pagination && wearables.data.pagination.totalElements ? wearables.data.pagination.totalElements : 0;
    
    return (
      <div>
        <PresentationalViewHeader
          themeName={themeName}
          onNewEntity={canManageWearables ? () => this.onNewWearable() : null}
          newEntityTitle="newWearable"
          searchPlaceholderEntityName="searchWearableDeviceId"
          searchNameContainerStyle={{ width: 220 }}
          newEntityIconName="icon-simple-line-icons-plus"
          isLoading={viewLoading}
          onSelectGuest={user => this.onSetFilter('userId', user && user.value)}
          onFilterChange={value => this.onSetFilter('deviceId', _.trim(value))}
          onSearchReset={() => this.onResetWearablesFilters()}
          onInfo={() => this.onShowWearableInfoPage()}
          onExportElements={format => this.onExportWearables(format)}
          numberOfElements={numberOfWearables}
          entitiesNumberSection={
            <div style={{ marginRight: 20, marginTop: 13, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <WearableIconCustom style={{ width: 25 }} />
              <h4 style={{ marginTop: 0 }}>{numberOfWearables}</h4>
            </div>
          }
        />
        <WearableTableView
          onNewWearable={() => this.onNewWearable()}
          onDeleteWearable={wearableId => this.onDeleteWearableRequest(wearableId)}
          onSelectWearable={wearable => this.onSelectWearable(wearable)}
          onRefreshWearables={() => this.onRefreshWearables()}
          onFetchWearablesOnPage={page => this.onFetchWearablesOnPage(page)}
        />
        <OperationalView
          themeName={themeName}
          isVisible={isOperationalMode}
          onClose={() => this.onCloseOperationalSection()}
          style={{ margin: 0, padding: 0 }}
          title={<Entity entity="wearableDevice" />}
        >
          <WearableOperationalSection
            onSubmitWearableForm={wearableData => this.onSaveWearableData(wearableData)}
            onSelectGuest={guest => this.onSelectGuest(guest)}
            onSelectAllowedAreas={areas => this.onSelectAllowedAreas(areas)}
            onSelectNotAllowedAreas={areas => this.onSelectNotAllowedAreas(areas)}
          />
        </OperationalView>
      </div>
    );
  }
} 

export default WearableManagement;
